import { post, get } from "../http/platformServer";
import { cloneDeep } from "lodash";
import ShippingTemplateExportRecordDTO from "@/dto/manualOrder/ShippingTemplateExportRecordDTO";
import ManualOrderAddRecordDTO from "@/dto/manualOrder/ManualOrderAddRecordDTO";
import ManualOrderConsigneeDTO from "@/dto/manualOrder/ManualOrderConsigneeDTO";
import qs from "qs";
import { PremiumFunctionType } from "../PremiumValidator";
import WebsiteType from "@/enum/WebsiteType";
import ShopType from "@/enum/ShopType";
import OrderDTO from "@/dto/order/OrderDTO";
import PaymentType from "@/enum/PaymentType";
import TaskStatusEnum from "@/enum/TaskStatus";

interface ImportOrderParams {
    file: Blob;
    shopId: number;
    websiteType: WebsiteType;
    shippingAddressId?: number;
    filePassword?: string;
    shipperName?: string;

    /** 订单源平台店铺ID */
    sourceShopId?: number;

    /** 订单源平台店铺类型 */
    sourceShopType?: ShopType;
}

// 批量导入订单
const importOrder = ({
    file,
    shopId,
    websiteType,
    shippingAddressId,
    filePassword,
    shipperName,
    sourceShopId,
    sourceShopType,
}: ImportOrderParams) => {
    const formData = new FormData();
    formData.append("file", file);
    /** @ts-ignore */
    formData.append("shopId", shopId);
    formData.append("websiteType", websiteType);

    if (shippingAddressId) {
        /** @ts-ignore */
        formData.append("shippingAddressId", shippingAddressId);
    }

    if (shipperName) {
        formData.append("shipperName", shipperName);
    }

    if (filePassword) {
        formData.append("filePassword", filePassword);
    }

    if (sourceShopId) {
        /** @ts-ignore */
        formData.append("sourceShopId", sourceShopId);
    }

    if (sourceShopType) {
        formData.append("sourceShopType", sourceShopType);
    }

    return post(shopId, `/manual_order/import`, formData, { timeout: 60000 });
};

interface QnImportOrderParams {
    excelFileOriginalName: string;
    excelFileSize: number;
    excelFileUrl: string;
    shopId: number;
    websiteType: WebsiteType;
    shippingAddressId?: number;
    filePassword?: string;
    shipperName?: string;

    sourceShopId?: number;
    sourceShopType?: ShopType;
}

// 批量导入订单（千牛专用）
export const qnImportOrder = (params: QnImportOrderParams) => {
    return post(params.shopId, `/manual_order/qianniu/import`, params);
};

const filterNullString = (orderDTO) => {
    const order = cloneDeep(orderDTO);

    Object.keys(order).forEach((key) => {
        if (order[key] === "") delete order[key];
    });

    return order;
};

/**
 * 订单信息
 *
 * OrderDto
 */
export interface OrderDto {
    /**
     * 买家昵称
     */
    buyerNickname?: null | string;
    /**
     * 买家备注
     */
    buyerRemarks?: null | string;
    /**
     * 买家用户名（买家唯一标识）
     */
    buyerUsername?: null | string;
    /**
     * 城市
     * 如果是复制平台订单创建手工订单时必传，尽量使用原平台的值，否则只传cityCode即可
     */
    city?: null | string;
    /**
     * 城市编码
     */
    cityCode?: number | null;
    /**
     * 代收货款
     */
    codAmounts?: number | null;
    /**
     * 收件人姓名
     */
    consigneeName?: null | string;
    /**
     * 订单折扣
     */
    discounts?: number | null;
    /**
     * 区县
     * 如果是复制平台订单创建手工订单时必传，尽量使用原平台的值，否则只传cityCode即可
     */
    district?: null | string;
    /**
     * 区县编码
     */
    districtCode?: number | null;
    /**
     * 手机号
     */
    mobile?: null | string;
    orderItems?: ManualOrderItemDto[] | null;
    /**
     * 原平台订单编号
     */
    originalId?: null | string;
    /**
     * 付款类型
     */
    paymentType?: PaymentType;
    /**
     * 省份
     * 如果是复制平台订单创建手工订单时必传，尽量使用原平台的值，否则只传provinceCode即可
     */
    province?: null | string;
    /**
     * 省份编码
     */
    provinceCode?: number | null;
    /**
     * 卖家备注
     */
    sellerRemarks?: null | string;
    /**
     * 发货人名称
     */
    shipperName?: null | string;
    /**
     * 发货人ID
     */
    shippingAddressId?: number | null;
    /**
     * 运费
     */
    shippingCost?: number | null;
    /**
     * 订单来源平台
     */
    shopType?: ShopType;
    /**
     * 源平台订单信息
     * 用于复制源平台订单创建加密手工订单，不是创建加密订单时传null
     */
    sourceOrderInfo?: SourceOrderInfoDto;
    /**
     * 详细地址
     */
    street?: null | string;
    /**
     * 固话
     */
    telephone?: null | string;
    /**
     * 街道办/镇
     */
    town?: null | string;
    /**
     * 订单重量，单位：kg
     */
    weight?: number | null;
    /**
     * 邮政编码
     * 「已废弃」
     */
    zipCode?: null | string;

    markId?: number;

    [property: string]: any;
}

/**
 * ManualOrderItemDto
 */
export interface ManualOrderItemDto {
    /**
     * 图片url
     */
    picture?: null | string;
    /**
     * 单价
     */
    price?: number | null;
    /**
     * 商品源平台ID
     */
    productOriginalId?: null | string;

    skuOriginalId?: null | string;

    /**
     * 数量
     */
    quantity?: number | null;
    /**
     * Sku编码
     */
    skuExternalId?: null | string;
    /**
     * 商品规格
     */
    skuProperties?: null | string;
    /**
     * 商品标题
     */
    title?: null | string;
    /**
     * 重量，单位：kg
     */
    weight?: number | null;
    [property: string]: any;
}

export enum SellerType {
    JdDropship = "JD_DROPSHIP",
    JdExpressAuth = "JD_EXPRESS_AUTH",
    JdSop = "JD_SOP",
    Taobao = "TAOBAO",
    Taote = "TAOTE",
    Tmall = "TMALL",
}

/**
 * 源平台订单信息
 * 用于复制源平台订单创建加密手工订单，不是创建加密订单时传null
 *
 * SourceOrderInfoDto
 */
export interface SourceOrderInfoDto {
    /**
     * 源订单加密地址ID
     */
    addressId?: null | string;
    /**
     * 城市
     */
    city?: null | string;
    /**
     * 收货人姓名
     */
    consigneeName?: null | string;
    /**
     * 区县
     */
    district?: null | string;
    /**
     * 手机号
     */
    mobile?: null | string;
    /**
     * 源订单手机号加密索引串
     */
    mobileEncryptionIndex?: null | string;
    /**
     * 源订单收货人姓名加密索引串
     */
    nameEncryptionIndex?: null | string;
    /**
     * 源订单编号，平台真实订单编号，如果是合并父订单或者拆分子订单时需获取真实的订单编号
     */
    orderOriginalId?: null | string;
    /**
     * 省份
     */
    province?: null | string;
    /**
     * 源商家类型
     */
    sellerType?: SellerType;
    /**
     * 源店铺账号
     */
    sellerUsername?: null | string;
    /**
     * 源订单店铺ID
     */
    shopId?: number | null;
    /**
     * 源店铺名称
     */
    shopName?: null | string;
    /**
     * 源店铺类型
     */
    shopType?: ShopType;
    /**
     * 详细地址
     */
    street?: null | string;
    /**
     * 源订单详细地址加密索引串
     */
    streetEncryptionIndex?: null | string;
    /**
     * 固话
     */
    telephone?: null | string;
    /**
     * 源订单固话加密索引串
     */
    telephoneEncryptionIndex?: null | string;
    /**
     * 街道办/镇
     */
    town?: null | string;
}

interface ManualAddOrderParams {
    shopId: number;
    orderDto: OrderDto;
    saveAndPrint?: boolean;
}

interface BatchManualAddOrderParams {
    shopId: number;
    orders: OrderDto[];
    saveAndPrint?: boolean;
}

export interface AddManualOrderResult {
    success: boolean;
    message: string;
    consigneeInfo: string;
    order: OrderDTO;
}

export function batchManualAddOrder(params: BatchManualAddOrderParams) {
    const { shopId, orders, saveAndPrint } = params;

    return post<AddManualOrderResult[]>(
        shopId,
        `/manual_order/batch_add`,
        {
            orders: orders.map((order) => filterNullString(order)),
            shopId,
            saveAndPrint,
        },
        {
            pathPremiumValidator() {
                return {
                    shopId,
                    type: PremiumFunctionType.ORDER_VOLUME_LIMIT,
                };
            },
        },
    );
}

export type ShippingTemplateExportRecordReturn = {
    items: ShippingTemplateExportRecordDTO[];
    pageNo: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
};

export type ManualOrderAddRecordReturn = {
    items: ManualOrderAddRecordDTO[];
    pageNo: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
};

// 获取批量导入订单的记录
const getBatchImportRecords = ({ pageNo, pageSize, shopId }: any): Promise<ShippingTemplateExportRecordReturn> =>
    get(shopId, `/manual_order/import_records`, { pageNo, pageSize, shopId });

// 获取手工录入订单的记录
const getManualOrderRecords = ({ pageNo, pageSize, shopId }: any): Promise<ManualOrderAddRecordReturn> =>
    get(shopId, `/manual_order/add_records`, { pageNo, pageSize, shopId });

interface ExportShippingTemplateParams {
    shopId: number;
    orderIds: number[];
    websiteType: WebsiteType;
}

interface AsyncOperationTaskShippingTemplateExportResult {
    id?: string;
    taskId: string;
    shopId: number;
    taskStatus: TaskStatusEnum;
    result?: {
        downloadUrl?: string;
    };
    error?: string;
}

// 异步导出发货模板
export function asyncExportShippingTemplate(params: ExportShippingTemplateParams) {
    return post<AsyncOperationTaskShippingTemplateExportResult>(
        params.shopId,
        "/manual_order/shipping_template/async_export",
        params,
    );
}

// 获取异步导出发货模板结果
export function getShippingTemplateExportResult(shopId: number, taskId: string) {
    return get<AsyncOperationTaskShippingTemplateExportResult>(shopId, "/manual_order/get_shipping_template_export_result", {
        taskId,
    });
}

// 获取导出发货的记录(分页列表)
const exportDeliverRecords = ({ shopId, pageNo, pageSize }: any) =>
    get(shopId, "/manual_order/shipping_template/records", { pageNo, pageSize });

// 添加联系人
const addConsignee = ({ shopId, consignees }: { shopId: number; consignees: ManualOrderConsigneeDTO[] }) =>
    post(shopId, "/manual_order/consignee", { consignees, shopId });

export function batchRecognizeConsignee(shopId: number, consigneeInfos: string[]) {
    return post<ManualOrderConsigneeDTO[]>(shopId, "/manual_order/consignee/batch_recognize", { consigneeInfos });
}

export function submitInaccurateRecognitionFeedback(shopId: number, consigneeInfo: string) {
    return post(shopId, "/manual_order/consignee/inaccurate_recognition_feedback", { consigneeInfo, shopId });
}

// 删除联系人
const deleteConsignee = ({ shopId, consigneeIds }) =>
    post(shopId, "/manual_order/consignee/delete", { consigneeIds, shopId });

interface GetConsigneeListParams {
    consigneeName: string;

    pageNo: number;

    pageSize: number;

    shopId: number;
}

// 列出联系人
const getConsigneeList = ({
    consigneeName,
    pageNo,
    pageSize,
    shopId,
}: GetConsigneeListParams): Promise<{
    items: ManualOrderConsigneeDTO[];
    pageNo: number;
    pageSize: number;
    totalCount: number;
    totalPage: number;
}> =>
    get(
        shopId,
        "/manual_order/consignee/list",
        { consigneeName, pageNo, pageSize, shopId },
        {
            paramsSerializer: function (params) {
                return qs.stringify(params);
            },
        },
    );

export interface WebsiteDTO {
    name: string;

    /**
     * .xls|.xlsx
     */
    importFileSuffix: string;

    /**
     * 通用模板COMMON
     */
    websiteType: WebsiteType;
}

/**列出所有支持导入订单的网站 */
function getImportable(shopId: number) {
    return get<WebsiteDTO[]>(shopId, "/manual_order/website/importable");
}

/**列出所有支持导出发货模板的网站 */
function getExportable(shopId: number) {
    return get<WebsiteDTO[]>(shopId, "/manual_order/website/exportable");
}

/** 列出导入订单号创建手工订单的导入任务 */
export function getCopyFromEcOrderImportRecords({ pageNo, pageSize, shopId }) {
    return get<ShippingTemplateExportRecordReturn>(shopId, "/manual_order/import_records/copy_from_ec_orders", {
        pageNo,
        pageSize,
        shopId,
    });
}

interface CopySingleManualOrderParams {
    shopId: number;
    orderId: number;
    orderOriginalId: string;
    newOrderOriginalId: string;
    /** 复制后是否取消原订单。 */
    cancelOriginal: boolean;
}

export function copySingleManualOrder(params: CopySingleManualOrderParams) {
    return post(params.shopId, "/manual_order/copy_single", params);
}

export {
    importOrder,
    getBatchImportRecords,
    getManualOrderRecords,
    exportDeliverRecords,
    addConsignee,
    deleteConsignee,
    getConsigneeList,
    getImportable,
    getExportable,
};
